/**
 * Stripe Subscription Queries
 *
 * React Query hooks for creating subscriptions from pricebooks.
 * Used only when shouldUseGobodo() is true.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createStripeSubscription,
  type CreateStripeSubscriptionInput,
} from '../stripeSubscriptionService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';

/**
 * Create Stripe subscription mutation.
 * Supports pricebook path (pricebookIdOrKey) or single price (priceId + quantity).
 */
export function useCreateStripeSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateStripeSubscriptionInput) => {
      if (!shouldUseGobodo()) {
        throw new Error('Create subscription requires Gobodo API');
      }
      const result = await createStripeSubscription(input);
      if (result.failureException) throw new Error(result.failureException);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });
}
